import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import {
  PsrBatchDetailType,
  PsrRecordType,
} from '@nxsys/types/models/revenueFilings/psr';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetLeaverToSubmitPSRParams,
  GetLeaverToSubmitPSRResponse,
  GetPayrollByIdParams,
  GetPayrollByIdResponse,
  GetPayrollToSubmitPSRParams,
  GetPayrollToSubmitPSRResponse,
  GetPSRBatchDetailParams,
  GetPSRBatchListParams,
  GetPSRBatchListResponse,
  GetPSRListParams,
  GetPSRListResponse,
  ResubmitLeaverPsrByIdParams,
  ResubmitPSRParams,
  SubmitLeaverPSRParams,
  SubmitLeaverPSRResponse,
  SubmitPSRPayrollByIdsParams,
  SubmitPSRPayrollByIdsResponse
} from '../types';

export const getPsrRecords = createAsyncThunk<
  GetPSRListResponse,
  GetPSRListParams
>(
  'revenue/getPsrRecords',
  async (params: GetPSRListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.revenueFilings.psr, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPsrRecordsAll = createAsyncThunk<
  GetPSRListResponse,
  GetPSRListParams
>(
  'revenue/getPsrRecordsAll',
  async (params: GetPSRListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.revenueFilings.psr, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPsrRecordsError = createAsyncThunk<
  PsrRecordType[],
  GetPSRListParams
>(
  'revenue/getPsrRecordsError',
  async (params: GetPSRListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.revenueFilings.psr, {
        params: {
          ...params,
          isSuccess: false,
        },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitPsrRecords = createAsyncThunk<
  PsrRecordType[],
  ResubmitPSRParams
>(
  'revenue/resubmitPsrRecords',
  async (params: ResubmitPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.revenueFilings.resubmitPSR,
        {
          ids: params.ids,
          ...(params.allInBatchId
            ? { allInBatchId: params.allInBatchId }
            : {}),
        },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitLeaverPSRRecords = createAsyncThunk<
  PsrRecordType[],
  ResubmitPSRParams
>(
  'revenue/resubmitLeaverPSRRecords',
  async (params: ResubmitPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.employment.resubmitLeaver,
        {
          ids: params.ids,
        },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPayrollToSubmitPSR = createAsyncThunk<
  GetPayrollToSubmitPSRResponse,
  GetPayrollToSubmitPSRParams
>(
  'revenue/getPayrollToSubmitPSR',
  async (params: GetPayrollToSubmitPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.payroll.mainV2, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getLeaverToSubmitPSR = createAsyncThunk<
  GetLeaverToSubmitPSRResponse,
  GetLeaverToSubmitPSRParams
>(
  'revenue/getLeaverToSubmitPSR',
  async (params: GetLeaverToSubmitPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.employment.mainIreland, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const submitLeaverPSR = createAsyncThunk<
  SubmitLeaverPSRResponse,
  SubmitLeaverPSRParams
>(
  'revenue/submitLeaverPSR',
  async (params: SubmitLeaverPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.employment.leaver, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPayrollById = createAsyncThunk<
  GetPayrollByIdResponse,
  GetPayrollByIdParams
>(
  'revenue/getPayrollById',
  async (params: GetPayrollByIdParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.payroll.main}/${params.id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/**
 * NX-6140 (BUG-011): ask the backend what this selection will be filed as.
 *
 * The "Submission Detail" modal is the last screen before an irreversible
 * filing, and it used to build the payroll run reference, submission id and
 * line item ids here - rules that only lived in the backend. When the batch
 * work changed them the modal kept showing the old shape, so the screen people
 * check disagreed with what was sent.
 */
export const previewPSRSubmission = createAsyncThunk<
  any,
  { companyId: string; payrollIds: string[] }
>(
  'revenue/previewPSRSubmission',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.revenueFilings.psrPreview,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const submitPSRByPayrollIds = createAsyncThunk<
  SubmitPSRPayrollByIdsResponse,
  SubmitPSRPayrollByIdsParams
>(
  'revenue/submitPSRByPayrollIds',
  async (params: SubmitPSRPayrollByIdsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.revenueFilings.psr, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitLeaverPsrById = createAsyncThunk<
  PsrRecordType[],
  ResubmitLeaverPsrByIdParams
>(
  'revenue/resubmitLeaverPsrById',
  async (params: ResubmitLeaverPsrByIdParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.employment.resubmitLeaver}/${params.id}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/**
 * NX-6140: the filings list, one row per submission to Revenue.
 *
 * The per-worker endpoint stays for the drill-down and for everything filed
 * before batching, when each worker really was their own submission.
 */
export const getPsrBatches = createAsyncThunk<
  GetPSRBatchListResponse,
  GetPSRBatchListParams
>(
  'revenue/getPsrBatches',
  async (params: GetPSRBatchListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.revenueFilings.psrBatch, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/**
 * One submission with every payslip in it.
 *
 * `sync` asks Revenue again rather than serving the stored verdict - the
 * manual equivalent of waiting for the half-hourly sweep.
 */
/**
 * Every rejected payslip across the submissions the listing is showing.
 *
 * Takes the listing's own filters so the file matches the screen, and is not
 * paginated - a partial file of errors is worse than none.
 */
export const exportPsrBatchErrors = createAsyncThunk<
  PsrRecordType[],
  /** No page/size: the export is the whole set, not a page of it. */
  Omit<GetPSRBatchListParams, 'page' | 'size'>
>(
  'revenue/exportPsrBatchErrors',
  async (
    params: Omit<GetPSRBatchListParams, 'page' | 'size'>,
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.get(
        endPoints.revenueFilings.psrBatchErrors,
        { params },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPsrBatchDetail = createAsyncThunk<
  PsrBatchDetailType,
  GetPSRBatchDetailParams
>(
  'revenue/getPsrBatchDetail',
  async (
    { id, sync, page, size, status }: GetPSRBatchDetailParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.get(
        endPoints.revenueFilings.psrBatchDetail(id),
        {
          params: {
            ...(sync ? { sync: true } : {}),
            page: page ?? 0,
            size: size ?? 20,
            ...(status ? { status } : {}),
          },
        },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const revenueActs = {
  getPsrRecords,
  getPsrRecordsAll,
  getPsrRecordsError,
  resubmitPsrRecords,
  resubmitLeaverPSRRecords,
  getPayrollToSubmitPSR,
  getLeaverToSubmitPSR,
  submitLeaverPSR,
  getPayrollById,
  previewPSRSubmission,
  submitPSRByPayrollIds,
  resubmitLeaverPsrById,
  getPsrBatches,
  getPsrBatchDetail,
  exportPsrBatchErrors,
};
